import React, { useMemo } from 'react';
import { useNodeForm } from '../Components/NodeFormKit';
import useApprovedTemplates from '../Components/useApprovedTemplates';
import { Field, FormSection, SelectInput, TextInput } from './AgentFields';

const bodyParamCount = (template) => {
  const body = (template?.content || []).find((item) => item.type === 'BODY');
  return ((body?.text || '').match(/{{\s*\d+\s*}}/g) || []).length;
};

const headerMediaType = (template) => {
  const header = (template?.content || []).find((item) => item.type === 'HEADER');
  return ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(header?.format) ? header.format.toLowerCase() : '';
};

const hasDynamicUrl = (template) =>
  (template?.content || []).some((item) => item.type === 'BUTTONS' && (item.buttons || []).some((button) => button.type === 'URL' && /{{\s*1\s*}}/.test(button.url || '')));

const bodyText = (template) => (template?.content || []).find((item) => item.type === 'BODY')?.text || '';

const TemplateToolConfig = ({ tool, setConfig }) => {
  const { theme } = useNodeForm();
  const { templates, loading } = useApprovedTemplates();
  const conf = tool.config || {};
  const selected = useMemo(() => templates.find((option) => option.value === String(conf.templateId)), [templates, conf.templateId]);
  const params = conf.params || [];

  const choose = (value) => {
    const option = templates.find((item) => item.value === value);
    if (!option) {
      setConfig({ templateId: '', templateName: '', params: [], mediaType: '', mediaLink: '', mediaName: '', urlParam: '' });
      return;
    }
    const count = bodyParamCount(option.template);
    const mediaType = headerMediaType(option.template);
    setConfig({
      templateId: option.value,
      templateName: option.template?.name || '',
      params: Array.from({ length: count }, (_, index) => params[index] || { source: 'ai', value: '', description: '' }),
      mediaType,
      mediaLink: mediaType === conf.mediaType ? conf.mediaLink || '' : '',
      mediaName: mediaType === 'document' ? conf.mediaName || '' : '',
      urlParam: hasDynamicUrl(option.template) ? conf.urlParam || '' : '',
    });
  };

  const setParam = (index, patch) => setConfig({ params: params.map((param, i) => (i === index ? { ...param, ...patch } : param)) });

  return (
    <FormSection title="Plantilla" style={{ marginBottom: '14px' }}>
      <Field label="Plantilla aprobada" hint={loading ? 'Cargando plantillas…' : 'Solo aparecen las plantillas aprobadas y activas.'}>
        <SelectInput value={conf.templateId} onChange={choose} options={templates} placeholder="Seleccione" />
      </Field>

      {selected && (
        <div style={{ fontSize: '12px', lineHeight: 1.5, color: theme.textSecondary, whiteSpace: 'pre-wrap', padding: '10px', borderRadius: '8px', border: `1px solid ${theme.border}`, marginBottom: '14px' }}>
          {bodyText(selected.template)}
        </div>
      )}

      {params.map((param, index) => (
        <Field key={index} label={`Parámetro {{${index + 1}}}`}>
          <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
            <div style={{ width: '150px', flexShrink: 0 }}>
              <SelectInput
                value={param.source || 'ai'}
                onChange={(source) => setParam(index, { source })}
                options={[
                  { value: 'ai', label: 'Lo llena la IA' },
                  { value: 'fixed', label: 'Valor fijo' },
                ]}
              />
            </div>
            <div style={{ flex: 1 }}>
              {param.source === 'fixed' ? (
                <TextInput value={param.value} onChange={(value) => setParam(index, { value })} placeholder="Texto o {{nombre_usuario}}" />
              ) : (
                <TextInput value={param.description} onChange={(description) => setParam(index, { description })} placeholder="Qué debe poner la IA (ej. nombre del producto)" />
              )}
            </div>
          </div>
        </Field>
      ))}

      {conf.mediaType && (
        <Field label={`${conf.mediaType === 'image' ? 'Imagen' : conf.mediaType === 'video' ? 'Video' : 'Documento'} del encabezado`} hint="URL pública del archivo.">
          <TextInput value={conf.mediaLink} onChange={(mediaLink) => setConfig({ mediaLink })} placeholder="https://" mono />
          {conf.mediaType === 'document' && (
            <div style={{ marginTop: '6px' }}>
              <TextInput value={conf.mediaName} onChange={(mediaName) => setConfig({ mediaName })} placeholder="Nombre del archivo (ej. catalogo.pdf)" />
            </div>
          )}
        </Field>
      )}

      {selected && hasDynamicUrl(selected.template) && (
        <Field label="Final de la URL del botón" hint="Lo que reemplaza {{1}} en el enlace del botón." style={{ marginBottom: 0 }}>
          <TextInput value={conf.urlParam} onChange={(urlParam) => setConfig({ urlParam })} placeholder="promo-octubre" mono />
        </Field>
      )}
    </FormSection>
  );
};

export default TemplateToolConfig;
